// Canonical A2UI v1.0 wire examples — the single source shared by the render
// preview harness (A2UIPreview page) and manual verification. Same shape the
// backend `a2ui._run` emits and persists as `tool_ui`; the preview feeds these
// through the SAME `A2UISurfaceRenderer` the playground/user views use, so a
// surface that renders here renders identically in a real chat.
//
// The primitive component tree is collapsed into a single semantic card root
// (one of the semantic card types). These examples cover every card plus a
// pre-seeded FormCard (dataModel.form) and one legacy v0.9 flat surface that
// must still render through the primitive fallback.
import type { A2UIComponent, A2UISurface } from '../components/ChatComponents';

const CATALOG_ID = 'pg.meninblox.dev:health/v1';

const wire = (surfaceId: string, components: A2UIComponent[]): A2UISurface => ({
  version: 'v1.0',
  createSurface: { surfaceId, catalogId: CATALOG_ID, components },
});

export interface A2UIExample {
  key: string;
  label: string;
  note?: string;
  surface: A2UISurface;
}

export const A2UI_EXAMPLES: A2UIExample[] = [
  {
    key: 'feature_recommend',
    label: 'FeatureRecommendCard (기능 이동 · navigate)',
    note: 'cta 클릭 → 앱 내장 기능으로 이동. 대시보드는 read-only 데모라 navigate 는 no-op (로그 + 제출 표시, onSubmit 미호출).',
    surface: wire('FeatureRecommendCard_ab12cd34', [
      {
        id: 'root',
        component: 'FeatureRecommendCard',
        title: '무릎 통증 체크',
        subtitle: '달리기 후 무릎 통증이 반복되고 있어요. 간단한 문진으로 확인해봐요.',
        feature: 'running_pain_check',
        cta_label: '통증 체크 시작',
      },
    ]),
  },
  {
    key: 'event_confirm_plan',
    label: 'EventConfirmCard · 플랜 (period/days 흡수)',
    note: '여러 날짜에 걸친 events 를 가진 EventConfirmCard — 일자별 뷰는 FE 가 events 를 start_time 날짜로 그룹핑해 파생(days 필드 아님). 구 TrainingPlanCard 흡수.',
    surface: wire('EventConfirmCard_plan_7f3e9a01', [
      {
        id: 'root',
        component: 'EventConfirmCard',
        title: '이번 주 러닝 플랜',
        subtitle: '10K 대비 4주 빌드업 · 1주차',
        events: [
          { action: 'create', summary: '인터벌 400m x 8', start_time: '2026-07-21T06:00:00+09:00' },
          { action: 'create', summary: '지속주 40분 (6:00/km)', start_time: '2026-07-23T06:00:00+09:00' },
          { action: 'create', summary: '롱런 12km 이지 페이스', start_time: '2026-07-26T07:00:00+09:00' },
        ],
        approve_label: '이 계획으로 등록',
        change_label: '다시 짜줘',
      },
    ]),
  },
  {
    key: 'event_confirm',
    label: 'EventConfirmCard (일정 확정 · 승인/변경)',
    note: 'events 요약(summary + start_time) 렌더 후 승인/변경. 승인 → onSubmit(요약).',
    surface: wire('EventConfirmCard_c4d5e6f7', [
      {
        id: 'root',
        component: 'EventConfirmCard',
        title: '일정 등록 확인',
        subtitle: '아래 일정을 캘린더에 추가할까요?',
        events: [
          { summary: '병원 예약 - 정형외과', start_time: '2026-07-22T10:30:00+09:00' },
          { summary: '재활 운동', start_time: '2026-07-24T18:00:00+09:00' },
        ],
        approve_label: '네, 등록',
        change_label: '수정할게요',
      },
    ]),
  },
  {
    key: 'form_card',
    label: 'FormCard (문진 · 5개 입력 타입)',
    note: 'choice/slider/date/checkbox/text 입력을 path 로 양방향 바인딩. 제출 → 직렬화된 user_message 를 onSubmit.',
    surface: wire('FormCard_e8a1b2c3', [
      {
        id: 'root',
        component: 'FormCard',
        title: '컨디션 문진',
        subtitle: '오늘 상태를 알려주세요.',
        icon: 'clipboard',
        fields: [
          {
            key: 'pain_area',
            type: 'choice',
            label: '통증 부위',
            required: true,
            path: '/form/pain_area',
            mode: 'single',
            options: [
              { label: '무릎', value: 'knee' },
              { label: '발목', value: 'ankle' },
              { label: '허리', value: 'back' },
            ],
          },
          {
            key: 'pain_level',
            type: 'slider',
            label: '통증 강도',
            path: '/form/pain_level',
            min: 0,
            max: 10,
            step: 1,
            unit: '점',
            default: 3,
          },
          { key: 'onset_date', type: 'date', label: '증상 시작일', path: '/form/onset_date' },
          { key: 'swelling', type: 'checkbox', label: '부기 있음', path: '/form/swelling' },
          {
            key: 'note',
            type: 'text',
            label: '추가 메모',
            path: '/form/note',
            placeholder: '자유롭게 적어주세요',
            multiline: true,
          },
        ],
        submit: { label: '제출', event: { name: 'submit:condition_survey' } },
      },
    ]),
  },
  {
    key: 'form_card_seeded',
    label: 'FormCard + dataModel (기존 값 프리필)',
    note: 'surface.dataModel.form 이 로컬 폼 상태를 /form/{key} 로 시드 — 입력이 미리 채워진 채 렌더.',
    surface: {
      version: 'v1.0',
      createSurface: {
        surfaceId: 'FormCard_seed5678',
        catalogId: CATALOG_ID,
        components: [
          {
            id: 'root',
            component: 'FormCard',
            title: '목표 설정',
            subtitle: '기존 값이 채워져 있어요. 필요하면 바꿔주세요.',
            fields: [
              {
                key: 'goal',
                type: 'choice',
                label: '목표',
                path: '/form/goal',
                mode: 'single',
                options: [
                  { label: '5K', value: '5k' },
                  { label: '10K', value: '10k' },
                  { label: '하프', value: 'half' },
                ],
              },
              {
                key: 'weekly_km',
                type: 'slider',
                label: '주간 목표 거리',
                path: '/form/weekly_km',
                min: 0,
                max: 100,
                step: 5,
                unit: 'km',
              },
              { key: 'reminder', type: 'checkbox', label: '알림 받기', path: '/form/reminder' },
            ],
            submit: { label: '저장', event: { name: 'submit:goal_setting' } },
          },
        ],
        dataModel: { form: { goal: '10k', weekly_km: 30, reminder: true } },
      },
    },
  },
  {
    key: 'places_card',
    label: 'PlacesCard (장소 검색 · 지도+상세 N개)',
    note: '_run 이 find_places 원본으로 채운 places[](좌표+상세)·center/zoom (agent 는 placeIds 만). 지도(마커)+상세카드가 한 places[] 에서 파생 → 마커 탭→해당 카드 하이라이트. open_now 배지는 wire 미포함, FE 가 hours+tz_id 로 렌더시점 계산(N1). 지도 키 부재/로드실패 시 상세 목록만. attribution 코드 강제.',
    surface: wire('PlacesCard_ab12cd34', [
      {
        id: 'root',
        component: 'PlacesCard',
        provider: 'naver',
        center: { lat: 37.5016, lng: 127.0273 },
        zoom: 14,
        places: [
          {
            placeId: 'a3f_1',
            lat: 37.5050754,
            lng: 127.0245882,
            name: '강남 365 응급의료센터',
            category: '병원 · 응급의료',
            address: '서울 강남구 테헤란로 152',
            tel: '02-1234-5678',
            // 매일 24시간 → 렌더시점 무관 "영업중"(프리뷰 결정성). emergency → 응급실 배지.
            hours: [0, 1, 2, 3, 4, 5, 6].map((day) => ({ day, start_min: 0, end_min: 1440 })),
            tz_id: 'Asia/Seoul',
            emergency: true,
          },
          {
            placeId: 'a3f_3',
            lat: 37.4982,
            lng: 127.0301,
            name: '역삼동 온누리약국',
            category: '약국',
            address: '서울 강남구 역삼로 201',
            tel: '02-555-0100',
            tz_id: 'Asia/Seoul',
          },
        ],
      },
    ]),
  },
  {
    key: 'ask_user_question',
    label: 'AskUserQuestionCard (되묻기 · 3질문 배치 + 복수선택)',
    note: '박스형 카드가 아니라 대화 흐름에 인라인으로 렌더(칩+질문+옵션 버튼). 독립적인 미확정 사항은 한 카드에 배치로 묶어 사용자가 한 번에 답한다(최대 4질문). 스키마 범위를 덮도록 선택지 2·3·4개 + multiSelect 를 섞었다. 자유 입력란은 스키마에 없고 클라이언트가 항상 붙인다. 제출 버튼은 모든 질문에 답해야 활성화되고, 제출 → `[질문 응답]` + `- {header}: {label}` 줄로 user_message 전송(복수선택은 ", " 로 join).',
    surface: wire('AskUserQuestionCard_9f3a1b2c', [
      {
        id: 'root',
        component: 'AskUserQuestionCard',
        questions: [
          {
            question: '이번 주 훈련, 강도를 낮출까요 볼륨(거리)을 줄일까요?',
            header: '훈련 조정',
            options: [
              {
                label: '강도 낮추기 (추천)',
                description: '페이스를 늦추고 인터벌을 줄입니다. 거리는 유지, 부상 위험 관리에 적합.',
              },
              {
                label: '볼륨 줄이기',
                description: '주간 총 거리를 줄이고 페이스는 유지합니다. 시간이 부족할 때 적합.',
              },
            ],
            multiSelect: false,
          },
          {
            question: '어느 요일에 쉬는 게 좋을까요?',
            header: '휴식일',
            options: [
              { label: '목요일', description: '금요일 고강도 세션 전날 — 회복 시간 확보에 유리.' },
              { label: '일요일', description: '원래 장거리런 예정일을 통째로 대체합니다.' },
              { label: '상관없음', description: '컨디션을 보고 그날그날 정하도록 여지를 둡니다.' },
            ],
            multiSelect: false,
          },
          {
            question: '이번 주에 함께 챙기고 싶은 것이 있나요?',
            header: '병행 관리',
            options: [
              { label: '크로스트레이닝', description: '자전거·수영을 넣어 충격 없이 유산소를 유지합니다.' },
              { label: '근력 보강', description: '둔근·코어 위주 20분 세션을 주 2회 추가합니다.' },
              { label: '스트레칭 루틴', description: '러닝 후 10분 정리 운동을 일정에 함께 넣습니다.' },
              { label: '수면·회복 체크', description: '수면 시간을 기록해 다음 주 강도 조절에 씁니다.' },
            ],
            multiSelect: true,
          },
        ],
      },
    ]),
  },
  {
    key: 'legacy_v09',
    label: '레거시: v0.9 flat 프리미티브 (봉투 이전 영속분)',
    note: '봉투/카드 마이그레이션 이전에 저장된 primitive surface 도 계속 렌더 (surfaceComponents 폴백 + primitive 렌더 경로).',
    surface: {
      version: 'v0.9',
      surface_id: 'legacy_v09',
      components: [
        { id: 'txt', component: 'Text', variant: 'h3', text: '이전 형식 카드' },
        { id: 'btn', component: 'Button', text: '레거시 버튼', variant: 'primary', action: { event: { name: 'approve' } } },
      ],
    },
  },
];
